/**
 * Sort only the innings line markets within the provided list.
 *
 * Order:
 * 1) numbered overs markets (1st then 2nd, by overs asc)
 * 2) markets without overs number (Runs / Runs Line) — keep original order
 *
 * @template T extends { name: string }
 * @param {T[]} markets
 * @returns {T[]}
 */
export function sortLineMarketNavItems<T>(markets: any[]): T[] {
  if (!isLineMarketsNav(markets)) return markets;

  const INNINGS_RE = /^\s*(1st|2nd)\s+Innings\b/i;

  const inningsRank = (name: any) => {
    const m = name.match(/\b(1st|2nd)\b/i);
    return m?.[1].toLowerCase() === "1st" ? 1 : 2;
  };

  const oversValue = (name: any) => {
    const m = name.match(/(\d+)\s*Overs\b/i);
    return m ? Number(m[1]) : null;
  };

  const isInningsMarket = (name: any) => INNINGS_RE.test(name);

  const compare = (a: any, b: any) => {
    const aName = a.name ?? "";
    const bName = b.name ?? "";

    const aOvers = oversValue(aName);
    const bOvers = oversValue(bName);

    const aHas = aOvers !== null;
    const bHas = bOvers !== null;

    // 1️⃣ numbered markets first
    if (aHas !== bHas) return aHas ? -1 : 1;

    // 2️⃣ if both numbered → innings then overs
    if (aHas && bHas) {
      const ir = inningsRank(aName) - inningsRank(bName);
      if (ir !== 0) return ir;
      return aOvers - bOvers;
    }

    // 3️⃣ both non-number → keep original order
    return 0;
  };

  // collect subset indices
  const indices = [];
  const subset = [];
  for (let i = 0; i < markets.length; i++) {
    const m = markets[i];
    if (m?.name && isInningsMarket(m.name)) {
      indices.push(i);
      subset.push(m);
    }
  }

  // stable sort (JS sort is stable in modern engines)
  subset.sort(compare);

  // reinsert
  const out = markets.slice();
  for (let i = 0; i < indices.length; i++) {
    out[indices[i]] = subset[i];
  }
  return out;
}

// {
//     "id": "1.254187880",
//     "name": "1st Innings 10 Overs Line",
//     "type": "MARKET",
//     "startTime": "2026-02-19T13:30:00.000Z",
//     "parents": [
//     {
//         "id": "4",
//         "name": "Cricket",
//         "type": "EVENT_TYPE",
//         "parents": [],
//         "aliases": {}
//     },
//     {
//         "id": "33439271",
//         "name": "    ICC Men's T20 World Cup",
//         "type": "EVENT",
//         "parents": [],
//         "aliases": {}
//     },
//     {
//         "id": "35278111",
//         "name": "Afghanistan v Canada",
//         "type": "EVENT",
//         "countryCode": "GB",
//         "parents": [],
//         "aliases": {}
//     }
// ],
//     "marketType": "1_INNING_10_OVR_LINE"
// }

export function isLineMarketsNav(data: any[]) {
  const isCricket = data?.some((nav: any) => {
    return nav?.parents?.some((parent: any) => parent?.id === "4");
  });
  const includesInnings = data?.some((nav: any) => nav?.name.includes("Innings"));

  return isCricket && includesInnings;
}
